import apiClient from './apiClient';

// Flag to determine if we should use mock data
const USE_MOCK_DATA = process.env.REACT_APP_USE_MOCK_DATA === 'true';

/**
 * Helper function to handle errors
 * @param {object} error - Error object
 * @returns {Error} - Formatted error
 */
const handleError = (error) => {
  const message =
    error.response?.data?.error?.message ||
    error.response?.data?.message ||
    error.message;
  if (message) {
    return new Error(message);
  }
  return error;
};

// Mock data for settings
const mockSettings = {
  general: {
    companyName: 'Acme Corporation',
    companyLogo: 'https://via.placeholder.com/150',
    companyEmail: 'info@acmecorp.com',
    companyPhone: '+1 (555) 123-4567',
    companyAddress: '123 Main Street, New York, NY 10001',
    companyWebsite: 'https://www.acmecorp.com',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h', // Options: '12h', '24h'
    defaultLanguage: 'en-US', // Options: 'en-US', 'sv-SE', etc.
    timezone: 'America/New_York'
  },
  timeTracking: {
    workWeekStart: 'monday', // Options: 'monday', 'sunday'
    minimumHours: 1,
    maximumHours: 24,
    timeIncrements: 15, // Options: 5, 10, 15, 30, 60 (minutes)
    overtimeRate: 1.5,
    allowFutureTimeEntries: true,
    lockPastTimeEntries: 7, // Days
    defaultProjectRequired: true,
    defaultApprovalRequired: true,
    roundTimeEntries: 'nearest', // Options: 'nearest', 'up', 'down', 'none'
  },
  approval: {
    autoApproveBelowHours: 8, // Auto-approve time entries below this threshold
    escalateAfterDays: 3, // Escalate unapproved time entries after X days
    requiredApprovers: 1, // Number of approvers required
    allowSelfApproval: false, // Allow employees to approve their own time
    enableCustomerApproval: true, // Allow customers to approve time entries
    notifyOnApproval: true, // Send notifications on approval
    notifyOnRejection: true, // Send notifications on rejection
  },
  notifications: {
    timeEntryReminders: true, // Send reminders for time entry
    reminderDay: 'friday', // Day to send reminders
    reminderTime: '16:00', // Time to send reminders
    approvalReminders: true, // Send reminders for pending approvals
    approvalReminderFrequency: 7, // Customer emails are always at least seven days apart
    emailNotifications: true, // Enable email notifications
    inAppNotifications: true, // Enable in-app notifications
    notificationDigest: 'daily', // Options: 'immediate', 'daily', 'weekly'
    digestTime: '08:00', // Time to send digest
  },
  security: {
    passwordExpiryDays: 90, // Password expiry in days (0 = never)
    mfaRequired: false, // Require multi-factor authentication
    sessionTimeout: 60, // Session timeout in minutes
    maxLoginAttempts: 5, // Maximum login attempts before lockout
    lockoutDuration: 30, // Lockout duration in minutes
    ipRestriction: false, // Restrict access by IP
    allowedIps: [], // List of allowed IPs
  },
  appearance: {
    theme: 'light', // Options: 'light', 'dark', 'auto'
    primaryColor: '#17627f',
    accentColor: '#adcb53',
    showWelcomeMessage: true,
    welcomeMessage: 'Welcome to TimeReporting!',
    dashboardLayout: 'default', // Options: 'default', 'compact', 'detailed'
    compactSidebar: false,
    enableAnimations: true,
  }
};

// Public app config consumable by any role (employee/customer/admin). Currently
// exposes the admin-configured week start day so the calendar/weekly grid can
// honour it. Falls back to Monday on any error so views always render.
export const getAppConfig = async () => {
  try {
    const response = await apiClient.get('/app-config');
    return response.data.data || { workWeekStart: 'monday' };
  } catch (error) {
    return { workWeekStart: 'monday' };
  }
};

// Get all settings
export const getSettings = async () => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ data: mockSettings });
      }, 500);
    });
  }
  
  try {
    const response = await apiClient.get('/settings');
    return { data: response.data.data, message: response.data.message };
  } catch (error) {
    throw handleError(error);
  }
};

// Get settings by category
export const getSettingsByCategory = async (category) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ data: mockSettings[category] || {} });
      }, 500);
    });
  }
  
  try {
    const response = await apiClient.get(`/settings/${category}`);
    return { data: response.data.data, message: response.data.message };
  } catch (error) {
    throw handleError(error);
  }
};

// Update settings
export const updateSettings = async (category, settings) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => {
        // In a real application, we would update the settings here
        resolve({ 
          data: { 
            ...mockSettings[category], 
            ...settings 
          },
          message: 'Settings updated successfully' 
        });
      }, 500);
    });
  }
  
  try {
    const response = await apiClient.put(`/settings/${category}`, settings);
    return { data: response.data.data, message: response.data.message };
  } catch (error) {
    throw handleError(error);
  }
};

// Reset settings to default for a category
export const resetSettings = async (category) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ 
          data: mockSettings[category],
          message: 'Settings with declared defaults reset successfully'
        });
      }, 500);
    });
  }
  
  try {
    const response = await apiClient.post(`/settings/${category}/reset`);
    return { data: response.data.data, message: response.data.message };
  } catch (error) {
    throw handleError(error);
  }
};

// Run the nightly reminder job now (admin test of the report email config).
export const runDailyReminders = async () => {
  try {
    const response = await apiClient.post('/admin/reminders/run-daily', { force: true });
    return response.data.data;
  } catch (error) {
    throw handleError(error);
  }
};

// Export settings as JSON
export const exportSettings = async () => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ 
          data: mockSettings,
          message: 'Settings exported successfully' 
        });
      }, 500);
    });
  }
  
  try {
    const response = await apiClient.get('/settings/export');
    return { data: response.data.data, message: response.data.message };
  } catch (error) {
    throw handleError(error);
  }
};

// Import settings from JSON
export const importSettings = async (settingsData) => {
  if (USE_MOCK_DATA) {
    return new Promise((resolve) => {
      setTimeout(() => {
        resolve({ 
          data: settingsData,
          message: 'Settings imported successfully' 
        });
      }, 500);
    });
  }
  
  try {
    const response = await apiClient.post('/settings/import', settingsData);
    return { data: response.data.data, message: response.data.message };
  } catch (error) {
    throw handleError(error);
  }
};
